import {
  faCircleCheck,
  faCloudArrowUp,
  faSpinner,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { IconButton } from '@/components/shared/IconButton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useTranslator } from '@/hooks/useTranslator';
import { useUploadStore } from '@/store/uploadStore';
import { SlotRow } from './SlotRow';
import type { UploadTrayButtonProps } from './types';

// ─── Tray button (for AppHeader) ──────────────────────────────────────────────

export function UploadTrayButton({ tooltip }: UploadTrayButtonProps) {
  const { getTranslation: t } = useTranslator({ page: 'shared' });
  const slots = useUploadStore(s => s.slots);
  const removeSlot = useUploadStore(s => s.removeSlot);
  const clearCompleted = useUploadStore(s => s.clearCompleted);
  const [open, setOpen] = useState(false);

  const slotList = Object.values(slots).sort(
    (a, b) => b.startedAt - a.startedAt,
  );
  const uploadingCount = slotList.filter(s => s.status === 'uploading').length;
  const hasCompleted = slotList.some(s => s.status !== 'uploading');

  const prevCountRef = useRef(slotList.length);
  useEffect(() => {
    // Auto-open the tray on the first upload, but never when a Dialog is
    // open — the popover would float above the modal otherwise.
    const dialogOpen =
      typeof document !== 'undefined' &&
      document.body.classList.contains('dialog-is-open');
    if (slotList.length > 0 && prevCountRef.current === 0 && !dialogOpen)
      setOpen(true);
    prevCountRef.current = slotList.length;
  }, [slotList.length]);

  // Force-close tray popover whenever a Dialog opens, so it doesn't float
  // above the modal.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const body = document.body;
    const observer = new MutationObserver(() => {
      if (body.classList.contains('dialog-is-open')) setOpen(false);
    });
    observer.observe(body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const headerLabel =
    uploadingCount > 0
      ? t('uploads.uploading').replace('{{count}}', String(uploadingCount))
      : slotList.length === 0
        ? t('uploads.tray')
        : t('uploads.complete');

  const triggerEl = (
    <IconButton
      variant='ghost'
      size='icon'
      tooltip={tooltip ?? t('uploads.tray')}
      className='h-7.5 w-7.5 text-muted-foreground'
    >
      <FontAwesomeIcon icon={faCloudArrowUp} className='size-3.5' />
    </IconButton>
  ) as React.ReactElement;

  return (
    <div className='relative'>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger render={triggerEl} />

        <PopoverContent
          align='end'
          sideOffset={8}
          className='w-72 p-0 overflow-hidden'
        >
          {/* Header */}
          <div className='flex items-center gap-2 px-3 py-2 bg-muted/50 border-b border-border'>
            {uploadingCount > 0 ? (
              <FontAwesomeIcon
                icon={faSpinner}
                className='size-3.5 text-primary animate-spin shrink-0'
              />
            ) : slotList.length > 0 ? (
              <FontAwesomeIcon
                icon={faCircleCheck}
                className='size-3.5 text-success shrink-0'
              />
            ) : (
              <FontAwesomeIcon
                icon={faCloudArrowUp}
                className='size-3.5 text-muted-foreground shrink-0'
              />
            )}
            <span className='text-xs font-medium flex-1 truncate'>
              {headerLabel}
            </span>
            {hasCompleted && (
              <button
                type='button'
                onClick={clearCompleted}
                className='text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0'
              >
                {t('uploads.clearDone')}
              </button>
            )}
          </div>

          {/* Slot list */}
          <div className='max-h-72 overflow-y-auto'>
            {slotList.length === 0 ? (
              <EmptyState
                icon={
                  <FontAwesomeIcon icon={faCloudArrowUp} className='size-5' />
                }
                title={t('uploads.empty')}
                className='py-6'
              />
            ) : (
              slotList.map(slot => (
                <SlotRow
                  key={slot.id}
                  slot={slot}
                  t={t}
                  onRemove={() => removeSlot(slot.id)}
                />
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Upload count badge */}
      {uploadingCount > 0 && (
        <span className='pointer-events-none absolute -top-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground tabular-nums'>
          {uploadingCount > 9 ? '9+' : uploadingCount}
        </span>
      )}
      {uploadingCount === 0 && slotList.length > 0 && (
        <span className='pointer-events-none absolute -top-0.5 -right-0.5 size-2 rounded-full bg-success' />
      )}
    </div>
  );
}
